/*
 * cf-shared — the small pieces every 3D surface on the site shares:
 * the Vitrine (carousel + build), the product page and the homepage.
 *
 *   CfShared.ease.inOut(t) / out(t)     the easing every glide uses
 *   CfShared.HERO_YAW                   the hero 3/4 pose, as an object yaw
 *                                       (0 = outside of the shoe, toe right,
 *                                       camera on +Z)
 *   CfShared.turn(o)                    the showcase turn (below)
 *   CfShared.holdEnv(u)                 speed envelope through a hold (0 → 1 → 0)
 *   CfShared.fetchBuffer(url, o)        fetch → ArrayBuffer, with a stall
 *                                       timeout and retries (slow phones)
 *   CfShared.slowNet()                  Save-Data or a 2G/3G connection
 *   CfShared.reducedMotion()
 *   CfShared.loader(el)                  the homepage loader's progress (below)
 *
 * Load it before anything that uses it; everything that uses it falls back
 * to a plain version when it's missing. Plain ES5 IIFE, like the Vitrine.
 */
(function (w) {
  'use strict';
  if (w.CfShared) return;

  var TAU = Math.PI * 2, DEG = Math.PI / 180;
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  var ease = {
    inOut: function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; },
    out:   function (t) { return 1 - Math.pow(1 - t, 3); }
  };
  function holdEnv(u) { return Math.sin(Math.PI * clamp(u, 0, 1)); }

  function reducedMotion() {
    try { return !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }

  // ── The showcase turn ────────────────────────────────────────────────────
  // One way of turning a shoe, used everywhere a shoe turns on its own (the
  // Vitrine carousel, the homepage pairs; the build's Play tour shares its
  // easing and hold envelope). A quarter turn at a time, eased in and out, then
  // a hold on the next 3/4 view while it keeps drifting the same way — so it
  // never stops dead and never spins like a turntable.
  //
  //   var t = CfShared.turn({ base: HERO_YAW });
  //   each frame: obj.rotation.y = t.update(dt);
  //   t.reset(yaw)   take over from wherever the object is now
  //   t.value()
  //
  // o: base (yaw of stop 0), stops (per turn, 4), glide (ms), hold (ms),
  //    drift (deg/s at the middle of a hold), dir (+1 / -1), from (start yaw)
  var TURN = { stops: 4, glide: 1700, hold: 2300, drift: 7 };
  function turn(o) {
    o = o || {};
    var base  = o.base != null ? o.base : 0;
    var step  = TAU / (o.stops || TURN.stops);
    var glide = o.glide || TURN.glide, hold = o.hold || TURN.hold;
    var drift = (o.drift != null ? o.drift : TURN.drift) * DEG;
    var dir   = o.dir < 0 ? -1 : 1;
    var still = reducedMotion();
    var yaw, phase, t, from, to;

    // The next stop ahead of y — at least 40% of a step ahead, so a glide is
    // always a real turn and never a nudge.
    function nextStop(y) {
      var k = Math.floor(dir * (y - base) / step + 1e-6) + 1;
      var s = base + dir * k * step;
      if (dir * (s - y) < step * 0.4) s += dir * step;
      return s;
    }
    // Take over at y: glide to the nearest stop if it's off one, else hold.
    function reset(y) {
      yaw = y != null ? y : base;
      var k = Math.round((yaw - base) / step), s = base + k * step;
      if (Math.abs(yaw - s) < 0.12) { phase = 'hold'; t = 0; }
      else { phase = 'glide'; t = 0; from = yaw; to = s; }
    }
    function update(dt) {
      if (still || !(dt > 0)) return yaw;
      t += dt * 1000;
      if (phase === 'glide') {
        var u = clamp(t / glide, 0, 1);
        yaw = from + (to - from) * ease.inOut(u);
        if (u >= 1) { phase = 'hold'; t = 0; }
      } else {
        yaw += dir * drift * dt * holdEnv(t / hold);
        if (t >= hold) { phase = 'glide'; t = 0; from = yaw; to = nextStop(yaw); }
      }
      return yaw;
    }
    reset(o.from);
    return { update: update, reset: reset, value: function () { return yaw; } };
  }

  // ── The homepage loader (snippets/cf-home-loader) ───────────────────────
  // var l = CfShared.loader(el): l.pct(0..100) inks the wordmark (never
  // backwards), l.name(i) inks the i-th name — one at a time, a beat apart,
  // even when two land together — and l.done(fn) lets the page go once the
  // last name has inked, after a beat at 100%. A missing el is a no-op.
  function loader(el) {
    var pctEl = el && el.querySelector('[data-ldr-pct]');
    var names = el ? [].slice.call(el.querySelectorAll('[data-ldr-name]')) : [];
    var q = [], timer = 0, shown = 0, over = false, still = reducedMotion();
    function pump() {
      if (timer || !q.length) return;
      var n = names[q.shift()];
      if (n) n.classList.add('is-in');
      timer = setTimeout(function () { timer = 0; pump(); }, still ? 0 : 520);
    }
    var api = {
      pct: function (p) {
        p = Math.round(clamp(p, 0, 100));
        if (p <= shown) return;
        shown = p;
        if (pctEl) pctEl.textContent = p + '%';
        if (el) el.style.setProperty('--ldr-load', p / 100);
      },
      name: function (i) { q.push(i); pump(); },
      done: function (fn) {
        if (over) return;
        if (q.length || timer) { setTimeout(function () { api.done(fn); }, 250); return; }
        over = true;
        api.pct(100);
        setTimeout(function () {
          if (el) el.classList.add('is-done');
          document.documentElement.classList.remove('cf-ldr-wait');
          if (fn) fn();
        }, still ? 0 : 900);
      },
      isDone: function () { return over; }
    };
    return api;
  }

  // ── Downloads that survive a bad connection ──────────────────────────────
  // fetch → ArrayBuffer. A request that sends no bytes for `stall` ms is
  // aborted and tried again (phones on a weak signal stall far more often than
  // they fail); `tries` attempts in all, backing off between them.
  // o: tries (4), stall (ms; longer on a slow connection), onProgress(loaded,
  //    total), priority ('high' | 'low' | 'auto' — fetch priority hint)
  function slowNet() {
    var c = w.navigator && (navigator.connection || navigator.mozConnection || navigator.webkitConnection);
    return !!(c && (c.saveData || /(^|-)2g|3g/.test(c.effectiveType || '')));
  }
  function fetchBuffer(url, o) {
    o = o || {};
    var tries = o.tries || 4;
    var stall = o.stall || (slowNet() ? 30000 : 15000);
    var attempt = 0;
    function once() {
      attempt++;
      var ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = null, dead = false;
      function arm() {
        clearTimeout(timer);
        timer = setTimeout(function () { dead = true; if (ctl) ctl.abort(); }, stall);
      }
      arm();
      var init = { credentials: 'same-origin' };
      if (ctl) init.signal = ctl.signal;
      if (o.priority) init.priority = o.priority;
      return fetch(url, init).then(function (res) {
        if (!res.ok) { var e = new Error('fetch ' + res.status + ' ' + url); e.status = res.status; throw e; }
        var total = +(res.headers.get('content-length') || 0);
        // Stream it when we can, so a stall mid-file is caught too.
        if (!res.body || !res.body.getReader) { return res.arrayBuffer(); }
        var reader = res.body.getReader(), chunks = [], got = 0;
        return (function pump() {
          return reader.read().then(function (r) {
            if (r.done) {
              var out = new Uint8Array(got), off = 0;
              for (var i = 0; i < chunks.length; i++) { out.set(chunks[i], off); off += chunks[i].length; }
              return out.buffer;
            }
            chunks.push(r.value); got += r.value.length; arm();
            if (o.onProgress) { try { o.onProgress(got, total); } catch (e) {} }
            return pump();
          });
        })();
      }).then(function (buf) {
        clearTimeout(timer);
        return buf;
      }, function (err) {
        clearTimeout(timer);
        // 4xx won't get better by asking again.
        var retry = attempt < tries && !(err && err.status >= 400 && err.status < 500);
        if (!retry) throw err;
        if (dead) console.warn('[cf] download stalled, retrying:', url);
        return new Promise(function (r) { setTimeout(r, 900 * Math.pow(2, attempt - 1)); }).then(once);
      });
    }
    return once();
  }

  w.CfShared = {
    ease: ease,
    holdEnv: holdEnv,
    HERO_YAW: -0.9,
    TURN: TURN,
    turn: turn,
    fetchBuffer: fetchBuffer,
    slowNet: slowNet,
    reducedMotion: reducedMotion,
    loader: loader
  };
})(window);
